"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { hideSupportChat, restoreSupportChat } from "../../hideSupportChat";
import { ensureIocBotOverlayStyles } from "../../nav_ai/ioc_bot_overlay";
import { validateChatHistory } from "../../nav_ai/staff_bot_setup";
import { STCComponentMixin } from "../../common/mixins/stc_component_mixin";
export let ReportFinderKnowledgeBot = class extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.privacyStatement = "";
    this.greeting = "";
    this.protocol = "";
    this.baseUrl = "";
    this.applicationType = "STC";
    this.chatId = "1";
    this.inputSuffix = "";
    this.isBotOpen = false;
    this.pointCutsRegistered = false;
    /** Match profile / preview: feature flags, controls, history, warm-starts; settings enable history UI + skip backend status probe. */
    this.reportBotPlugins = [
      "n360-feature-flags",
      "message-controls",
      "n360-chat-history",
      "warm-starts"
    ];
    this.reportBotSettings = {
      hidden: { heading_close: false },
      rules: { force_links: "to-same-tab" },
      allow: {
        chat_history: true,
        user_feedback: true,
        lock_on_send: true
      },
      network_settings: { validate_backend_support: false }
    };
    this.blockquoteRegex = new RegExp(`</?blockquote>`, "g");
    /**
     * Report deep-links include `?ai=1`. Prefer the bot's `click-notified` event over
     * querying shadowRoot — that root goes away when the bot moves to light DOM.
     */
    this.handleBotClickNotified = (evt) => {
      const original = evt.detail?.event;
      const target = original?.target ?? evt.detail?.srcElement;
      const link = target?.closest?.('a[href*="?ai=1"]');
      if (!link) return;
      original?.preventDefault?.();
      evt.preventDefault();
      const messages = this.knowledgeBot?.bot?.messages?.messages?.slice(2) || [];
      let conversationText = "";
      messages.forEach((obj) => {
        if (obj.raw) {
          conversationText += obj.type === "from-bot" ? "Chatbot: " : "Human: ";
          conversationText += `${obj.raw}
`;
        }
      });
      localStorage["ai_report_finder_context"] = conversationText;
      const a = document.createElement("a");
      a.href = link.href;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    /**
     * The bot header close button dispatches `close-ended` then calls `remove()` on itself
     * (see IocBotElement.__closeHeader__onClick). That does not update this host — without
     * this handler, `isBotOpen` stays true (Lit would remount the bot) and support chat
     * stays hidden.
     */
    this.handleClose = () => {
      if (!this.isBotOpen) return;
      this.isBotOpen = false;
      this.pointCutsRegistered = false;
      restoreSupportChat();
    };
  }
  handlePreviewClick() {
    ensureIocBotOverlayStyles();
    this.isBotOpen = true;
  }
  registerPointCutsIfReady() {
    if (this.pointCutsRegistered || !this.isBotOpen) return;
    const bot = this.knowledgeBot?.bot;
    if (!bot?.pointCut) return;
    this.pointCutsRegistered = true;
    bot.pointCut.add("before", "send", () => {
      const botEl = this.knowledgeBot;
      botEl.clearUserInput?.();
    });
    bot.pointCut.add("before", "render-message-block", (message) => {
      if (message.type === "from-bot") {
        message.content = message.content.replace(this.blockquoteRegex, "");
      }
      return message;
    });
    validateChatHistory(this.knowledgeBot);
  }
  updated(changedProperties) {
    if (changedProperties.has("isBotOpen") && this.isBotOpen) {
      hideSupportChat();
      if (!this.pointCutsRegistered) {
        void this.updateComplete.then(() => this.registerPointCutsIfReady());
      }
    }
    this.registerPointCutsIfReady();
  }
  /** Fixed overlay shell via eab-ioc-bot.overlay (no hi-drawer). */
  renderKnowledgeBot() {
    if (!this.isBotOpen) return "";
    return html`
      <eab-ioc-bot
        class="overlay"
        id="report-finder-knowledge-bot"
        image="https://cdn.dev.eab.com/assets/2018-07-11/logo/logo-circle.png"
        heading="Chat with AI Report Finder"
        placeholder="Ask a question"
        .plugins=${[...this.reportBotPlugins]}
        .settings=${this.reportBotSettings}
        .prompts=${[
      { html: this.privacyStatement },
      { html: this.greeting }
    ]}
        uri=${this.protocol}
        .uriFn=${() => `${this.baseUrl}/report_finder?application_type=${this.applicationType}&chat_id=${this.chatId}`}
        .uriSettings=${{
      send: {
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        }
      }
    }}
        @click-notified=${this.handleBotClickNotified}
        @close-ended=${this.handleClose}
      >
      </eab-ioc-bot>
    `;
  }
  render() {
    return html`
      <div class="mb-4 ai_report_finder_button">
        <hi-tile>
          <h3>
            <hi-icon glyph="ai" theme="primary" class="mrs" aria-hidden="true"></hi-icon>
            AI Report Finder
          </h3>
          <div id="ai-filter-assistant-instructions${this.inputSuffix}">
            <p class="mbm">
              Hi there! I’m Report Finder, an AI-powered assistant that helps you choose the right report and apply filters for you.
            </p>
            <p class="mbm">
              What are you trying to find?<br>
              You can tell me what data you’re looking for and any filters you’d like to use, and I’ll recommend the best report and apply those filters automatically.
            </p>
          </div>
          <hi-button
            id="ai_report_finder_button${this.inputSuffix}"
            aesthetic="secondary"
            @trigger-ended=${this.handlePreviewClick}
          >
            <hi-icon glyph="ai" theme="primary" aria-hidden="true"></hi-icon>
            <span>AI Report Finder</span>
          </hi-button>
        </hi-tile>
      </div>
      ${this.renderKnowledgeBot()}
    `;
  }
};
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "privacyStatement", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "greeting", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "protocol", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "baseUrl", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "applicationType", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "chatId", 2);
__decorateClass([
  property({ type: String })
], ReportFinderKnowledgeBot.prototype, "inputSuffix", 2);
__decorateClass([
  state()
], ReportFinderKnowledgeBot.prototype, "isBotOpen", 2);
__decorateClass([
  query("#report-finder-knowledge-bot")
], ReportFinderKnowledgeBot.prototype, "knowledgeBot", 2);
ReportFinderKnowledgeBot = __decorateClass([
  customElement("stc-report-finder-knowledge-bot")
], ReportFinderKnowledgeBot);
